import * as React from 'react';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Unstable_Grid2';
import { FmtpError, SortDirection, v2_parcelSearchParameters, v2_parcelSearchResult, v2_sortBy, Result } from '@lanterds/types';
import { useSnackbar, SnackbarKey } from 'notistack';
import { Box, Card } from '@mui/material';
import { useEffect, useState, useRef } from 'react';
import './ParcelSearch.scss';
import { postParcelsSearch } from 'src/api/parcelSearch';
import { ParcelSearchResultsGrid } from './ParcelSearchResultsGrid';
import { ParcelSearchForm } from './ParcelSearchForm';
import { DEFAULT_SORT_DIRECTION, DEFAULT_SORT_ORDER, DEFAULTS_PARAMS } from './types';
import { useLocation } from 'react-router-dom';

interface CallbacksShape {
    successCallback?: { (): void | undefined };
    errorCallback?: { (): void | undefined };
}

let currentRequestId = 0;

export default function ParcelSearch() {
    const [parcels, setParcels] = useState<v2_parcelSearchResult[] | null>(null);
    const [searchParams, setSearchParams] = useState<v2_parcelSearchParameters>({ ...DEFAULTS_PARAMS });
    const [isFetchingParcels, setIsFetchingParcels] = useState(false);
    const [shouldCondenseForm, setShouldCondenseForm] = useState(false);
    const [sortColumn, setSortColumn] = useState<v2_sortBy>(DEFAULT_SORT_ORDER);
    const [sortDirection, setSortDirection] = useState<SortDirection>(DEFAULT_SORT_DIRECTION);

    const location = useLocation();

    useEffect(() => {
        if (location && location.state) {
            setParcels(location.state['previousResults'] ?? null);
        }
    }, [location]);

    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const snackbarKey = useRef<SnackbarKey | null>(null);

    useEffect(() => {
        return () => {
            if (snackbarKey.current) {
                closeSnackbar(snackbarKey.current);
                snackbarKey.current = null;
            }
        };
    }, [closeSnackbar]);

    async function fetchParcels(params: v2_parcelSearchParameters, callbacks?: CallbacksShape) {
        const thisRequestId = ++currentRequestId;
        setSearchParams(params);
        setIsFetchingParcels(true);
        closeSnackbar();

        snackbarKey.current = enqueueSnackbar('Fetching parcels...', {
            variant: 'loading',
            preventDuplicate: true,
            disableWindowBlurListener: true,
            autoHideDuration: null,
            action: (key) => (
                <Button size="small" className="dismiss-button" onClick={() => closeSnackbar(key)}>
                    Dismiss
                </Button>
            ),
        });

        const parcelSearchResults: Result<v2_parcelSearchResult[]> = await postParcelsSearch(params).catch((err) => {
            console.error(err);
            if (callbacks?.errorCallback) callbacks.errorCallback();
            closeSnackbar();
            enqueueSnackbar(err.memo, {
                variant: 'error',
                preventDuplicate: true,
                action: (key) => (
                    <Button size="small" className="dismiss-button" onClick={() => closeSnackbar(key)}>
                        Dismiss
                    </Button>
                ),
            });
            return;
        });

        setIsFetchingParcels(false);
        closeSnackbar(snackbarKey.current);

        if (parcelSearchResults.error) {
            const error = parcelSearchResults.error;
            console.error(error);
            if (callbacks?.errorCallback) callbacks.errorCallback();
            closeSnackbar();
            enqueueSnackbar(error.memo, {
                variant: 'error',
                preventDuplicate: true,
                action: (key) => (
                    <Button size="small" className="dismiss-button" onClick={() => closeSnackbar(key)}>
                        Dismiss
                    </Button>
                ),
            });
            return;
        }

        const { data, warning } = parcelSearchResults;

        if (warning) {
            let message = '';
            if (warning instanceof FmtpError) {
                console.warn(warning.memo);
                message = warning.memo;
            }
            if (warning instanceof Array) {
                if (warning.length) {
                    message = warning[0].memo;
                }

                warning.forEach((warningItem: FmtpError) => {
                    console.warn(warningItem.memo);
                });
            }
            if (message) {
                enqueueSnackbar(message, {
                    variant: 'warning',
                    preventDuplicate: true,
                    action: (key) => (
                        <Button size="small" className="dismiss-button" onClick={() => closeSnackbar(key)}>
                            Dismiss
                        </Button>
                    ),
                });
            }
        }

        if (callbacks?.successCallback) callbacks.successCallback();
        currentRequestId === thisRequestId && setParcels(data);
    }

    const resetSearch = () => {
        closeSnackbar();
        setParcels(null);
        setIsFetchingParcels(false);
    };

    const handleSortClick = async (column: v2_sortBy, direction: SortDirection) => {
        resetSearch();
        const tmpParams: v2_parcelSearchParameters = {
            ...searchParams,
            sortBy: column,
            sortDirection: direction,
        };
        const tmpCallbacks = {
            successCallback: () => {
                setIsFetchingParcels(false);
            },
        };

        await fetchParcels(tmpParams, tmpCallbacks);
        setSearchParams(tmpParams);
        setSortColumn(column);
        setSortDirection(direction);
    };

    return (
        <Box id="v2-parcel-search-container" className="auto-fill-height">
            <Card id="parcel-search-form" className="auto-fill-height">
                <Grid container spacing={0}>
                    <Box className="search-details">
                        <ParcelSearchForm
                            isFetchingParcels={isFetchingParcels}
                            shouldCondenseForm={shouldCondenseForm}
                            fetchParcels={fetchParcels}
                            resetSearch={resetSearch}
                            searchParams={searchParams}
                        />
                    </Box>
                </Grid>
                {parcels ? (
                    <ParcelSearchResultsGrid
                        parcels={parcels}
                        isFetchingParcels={isFetchingParcels}
                        handleSortClick={handleSortClick}
                        setShouldCondenseForm={setShouldCondenseForm}
                        sortColumn={sortColumn}
                        sortDirection={sortDirection}
                    />
                ) : null}
            </Card>
        </Box>
    );
}
